import React, { useEffect, useRef, useState } from 'react';
import { Camera, Loader2, Trash2, Upload } from 'lucide-react';
import { toast } from 'sonner';
import http, { API } from '../api/http';

const errorMessage = (error, fallback) => {
  const detail = error.response?.data?.detail;
  return (typeof detail === 'string' ? detail : detail?.message) || fallback;
};

export default function WorksitePhotos({ worksiteId }) {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const alive = useRef(false);
  const url = `${API}/worksites/${worksiteId}/photos`;

  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const response = await http.get(url);
        if (!cancelled) { setPhotos(response.data); setError(''); }
      } catch (failure) {
        if (!cancelled) setError(errorMessage(failure, 'Impossible de charger les photos.'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [url]);

  const handleUpload = async (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = '';
    if (!files.length) return;
    setUploading(true);
    try {
      const formData = new FormData();
      files.forEach(file => formData.append('files', file));
      const response = await http.post(url, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (alive.current) setPhotos(previous => [...previous, ...response.data.photos]);
    } catch (failure) {
      toast.error(errorMessage(failure, 'Impossible d\u2019envoyer la photo.'));
    } finally {
      if (alive.current) setUploading(false);
    }
  };

  const handleDelete = async (photo) => {
    try {
      await http.delete(`${API}/schedules/${photo.schedule_id}/photos/${photo.id}`);
      if (alive.current) setPhotos(previous => previous.filter(item => item.id !== photo.id));
    } catch (failure) {
      toast.error(errorMessage(failure, 'Impossible de supprimer la photo.'));
    }
  };

  return (
    <section aria-label="Photos du chantier" className="min-w-0 border-t border-gray-200 pt-4 space-y-3">
      <div className="flex items-center justify-between gap-3">
        <h3 className="flex items-center gap-2 font-bold text-gray-900">
          <Camera className="h-5 w-5 text-cyan-600 shrink-0" /> Photos du chantier
          {photos.length > 0 && <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs font-bold">{photos.length}</span>}
        </h3>
        <label className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold cursor-pointer transition-colors ${uploading ? 'bg-gray-200 text-gray-400 cursor-wait' : 'bg-purple-600 text-white hover:bg-purple-700'}`}>
          {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
          {uploading ? 'Envoi...' : 'Ajouter'}
          <input type="file" accept="image/*" multiple className="hidden" disabled={uploading} onChange={handleUpload} aria-label="Ajouter des photos du chantier" />
        </label>
      </div>
      {error && <p role="alert" className="text-sm text-red-700">{error}</p>}
      {loading ? (
        <p role="status" className="text-sm text-gray-500">Chargement des photos...</p>
      ) : photos.length === 0 ? (
        <p className="text-sm text-gray-400 italic">Aucune photo pour le moment.</p>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {photos.map(photo => (
            <div key={photo.id} className="group relative rounded-lg overflow-hidden border border-gray-200 bg-gray-50">
              <a href={photo.file_url} target="_blank" rel="noopener noreferrer" className="block aspect-square overflow-hidden bg-gray-100">
                <img src={photo.file_url} alt={photo.file_name || 'Photo chantier'} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200" />
              </a>
              <button
                type="button"
                onClick={() => handleDelete(photo)}
                className="absolute top-1.5 right-1.5 bg-red-600 hover:bg-red-700 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity shadow"
                title="Supprimer"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
              <div className="p-1.5">
                <p className="text-[11px] text-gray-500 truncate" title={photo.uploaded_by_name}>{photo.uploaded_by_name}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
